import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  Box,
  Button,
  CircularProgress,
  FormHelperText,
  IconButton,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { useState } from 'react';
import { ErrorCode, FileRejection, useDropzone } from 'react-dropzone';

import { CloseIcon } from 'src/assets/icons/CloseIcon';

import { IMAGE_CONTENT_TYPES } from '../../utils/imageContentType';
import { StyledFileBox } from './style';

const MAX_FILE_SIZE = 10 * 1024 * 1024;
const REJECTION_MESSAGES: Partial<Record<string, string>> = {
  [ErrorCode.FileInvalidType]: 'Unsupported file type. Use an SVG, PNG or JPG image.',
  [ErrorCode.FileTooLarge]: 'The image is larger than 10MB.',
};
const REQUIREMENTS_MESSAGE = 'The selected file does not match requirements.';
const FILE_REQUIREMENTS = 'SVG, PNG or JPG, up to 10MB. Drop a file on the field or select one.';
const THUMBNAIL_HEIGHT = 28;

const getRejectionMessage = (rejection?: FileRejection) =>
  REJECTION_MESSAGES[rejection?.errors[0]?.code ?? ''] ?? REQUIREMENTS_MESSAGE;

type Props = {
  label: string;
  testId: string;
  previewUrl?: string;
  // Crops the preview to this width/height ratio; without it the whole image shows.
  previewAspectRatio?: number;
  // The last upload's failure, shown until the parent clears it.
  uploadError?: string;
  disabled?: boolean;
  onUpload: (file: File) => Promise<void>;
  onRemove: () => void;
  onBusyChange?: (busy: boolean) => void;
};

export const ImageUploadField = ({
  label,
  testId,
  previewUrl,
  previewAspectRatio,
  uploadError,
  disabled,
  onUpload,
  onRemove,
  onBusyChange,
}: Props) => {
  const theme = useTheme();
  const [isUploading, setIsUploading] = useState(false);
  const [rejectionError, setRejectionError] = useState('');
  // Only known for files picked here; a stored image has just its URL.
  const [fileName, setFileName] = useState('');

  const setBusy = (busy: boolean) => {
    setIsUploading(busy);
    onBusyChange?.(busy);
  };

  const onDrop = async (acceptedFiles: File[], fileRejections: FileRejection[]) => {
    const file = acceptedFiles[0];
    if (!file || fileRejections.length > 0) {
      setRejectionError(getRejectionMessage(fileRejections[0]));
      return;
    }

    setRejectionError('');
    setBusy(true);
    try {
      await onUpload(file);
      setFileName(file.name);
    } finally {
      setBusy(false);
    }
  };

  const isLocked = disabled || isUploading;

  const { getRootProps, getInputProps, open, isDragActive } = useDropzone({
    accept: IMAGE_CONTENT_TYPES,
    maxSize: MAX_FILE_SIZE,
    multiple: false,
    onDrop,
    disabled: isLocked,
    noClick: true,
    noKeyboard: true,
  });

  const removeHandler = () => {
    setRejectionError('');
    setFileName('');
    onRemove();
  };

  const renderFileContent = () => {
    if (isUploading) {
      return (
        <>
          <CircularProgress size={16} />
          <Typography variant="body2" noWrap>
            Uploading…
          </Typography>
        </>
      );
    }
    if (!previewUrl) {
      return (
        <Typography variant="body2" noWrap sx={{ color: theme.palette.grey[500] }}>
          No file selected
        </Typography>
      );
    }
    return (
      <>
        <Box
          component="img"
          src={previewUrl}
          alt={label}
          sx={
            previewAspectRatio
              ? {
                  height: THUMBNAIL_HEIGHT,
                  aspectRatio: `${previewAspectRatio}`,
                  objectFit: 'cover',
                }
              : { maxHeight: THUMBNAIL_HEIGHT, maxWidth: 48, objectFit: 'contain' }
          }
        />
        <Typography variant="body2" noWrap sx={{ flex: 1 }}>
          {fileName || 'Current image'}
        </Typography>
        <IconButton
          size="small"
          aria-label={`Remove ${label}`}
          data-sm={`${testId}-remove`}
          onClick={(event) => {
            event.stopPropagation();
            removeHandler();
          }}
          disabled={isLocked}
        >
          <CloseIcon width={10} />
        </IconButton>
      </>
    );
  };

  return (
    <Box data-sm={testId}>
      <Box
        {...getRootProps()}
        sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}
      >
        <input {...getInputProps({ disabled: isLocked })} data-testid={`${testId}-input`} />
        <Typography variant="body2" sx={{ width: '96px' }}>
          {label}
        </Typography>
        <StyledFileBox
          onClick={isLocked ? undefined : open}
          sx={{
            opacity: isLocked ? 0.6 : 1,
            cursor: isLocked ? 'default' : 'pointer',
            borderColor: isDragActive ? theme.palette.primary.main : undefined,
          }}
        >
          {renderFileContent()}
        </StyledFileBox>
        <Button
          variant="outlined"
          color="inherit"
          size="small"
          data-sm={`${testId}-select`}
          onClick={open}
          disabled={isLocked}
        >
          Select File
        </Button>
        <Tooltip title={FILE_REQUIREMENTS}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary' }}>
            <Typography variant="caption">File requirements</Typography>
            <InfoOutlinedIcon sx={{ fontSize: 16 }} />
          </Box>
        </Tooltip>
      </Box>
      {(rejectionError || uploadError) && (
        <FormHelperText error sx={{ ml: '112px' }}>
          {rejectionError || uploadError}
        </FormHelperText>
      )}
    </Box>
  );
};
